import { useEffect, useRef } from 'react';
import type { PosBook } from '../api/pos';
import { money } from '../format';
import { customerSummary, type CustomerDetailsValue } from './CustomerDetails';

export interface ConfirmLine {
  book: PosBook;
  quantity: number;
}

/**
 * The last look before the sale is banked.
 *
 * Customers change their mind when they hear the final figure, and until this
 * dialog existed the only way back from a completed bill was an admin void.
 * So the total gets read out here, big, and nothing is sent until staff say
 * so — cancelling is free, because the bill behind is left exactly as it was.
 */
export default function ConfirmSale({
  lines,
  subtotal,
  discount,
  couponCode,
  couponDiscount = 0,
  total,
  itemCount,
  paymentText,
  isTransfer,
  customer,
  saving,
  onCancel,
  onConfirm,
}: {
  lines: ConfirmLine[];
  /** What the lines come to, before anything is taken off. */
  subtotal: number;
  /** Rupees off the whole bill by the counter, or 0. */
  discount: number;
  /** A coupon the customer brought, and what it takes off, before `discount`. */
  couponCode?: string;
  couponDiscount?: number;
  /** `subtotal` less the coupon and `discount` — the figure to read out and to charge. */
  total: number;
  itemCount: number;
  /** How it is being paid, in words: "Cash", or "Cash ₹500 + UPI ₹1,000". */
  paymentText: string;
  /**
   * These books are going to the other shop rather than to a customer. The
   * dialog still reads the bill back — the point is to catch the wrong books
   * before they leave — but there is no figure to read out and no tender.
   */
  isTransfer?: boolean;
  customer: CustomerDetailsValue;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const offTotal = discount + couponDiscount;

  // Focus lands on "Go back", not on the confirm button, and that is
  // deliberate. A barcode scanner types its code and then sends Enter; with
  // the confirm button focused, one absent-minded scan over an open dialog
  // would bill the customer who just asked to wait. Enter here is always the
  // harmless direction — confirming takes a real click or a Tab.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Never leave staff trapped in the dialog while a request is in flight
      // — but do not let Escape cancel it either, the sale is already gone.
      if (e.key === 'Escape' && !saving) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, saving]);

  return (
    <div className="modal-backdrop" onClick={() => !saving && onCancel()}>
      <div
        className="modal modal-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-sale-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-sale-title" className="confirm-title">
          {isTransfer ? 'Confirm this shop transfer' : 'Confirm this sale'}
        </h2>
        <p className="muted small confirm-lede">
          Read the total back to the customer. Nothing is billed until you confirm.
        </p>

        <ul className="confirm-lines">
          {lines.map(({ book, quantity }) => (
            <li key={book.id}>
              <span className="confirm-line-title">{book.title}</span>
              <span className="confirm-line-qty">
                {quantity} × {money(book.price)}
              </span>
              <span className="confirm-line-total">
                {money(Number(book.price) * quantity)}
              </span>
            </li>
          ))}
        </ul>

        {/* The discount is spelled out here rather than folded into one
            figure. This dialog exists to be read back to the customer, and
            "eight hundred, less the fifty we said, so seven fifty" is the
            sentence — a bare 750 is the one thing they cannot check. */}
        {offTotal > 0 && (
          <div className="confirm-subtotals">
            <div className="confirm-subtotal-row">
              <span>
                {itemCount} item{itemCount === 1 ? '' : 's'}
              </span>
              <span>{money(subtotal)}</span>
            </div>
            {couponDiscount > 0 && (
              <div className="confirm-subtotal-row confirm-discount">
                <span>Coupon {couponCode}</span>
                <span>− {money(couponDiscount)}</span>
              </div>
            )}
            {discount > 0 && (
              <div className="confirm-subtotal-row confirm-discount">
                <span>Discount</span>
                <span>− {money(discount)}</span>
              </div>
            )}
          </div>
        )}

        <div className="confirm-total">
          <span>
            {isTransfer || offTotal === 0
              ? `${itemCount} item${itemCount === 1 ? '' : 's'}`
              : 'To pay'}
          </span>
          {/* An em dash rather than ₹0.00: nothing is being charged, and a
              zero is the one figure staff might read out to a customer. */}
          <strong>{isTransfer ? '—' : money(total)}</strong>
        </div>

        <dl className="confirm-meta">
          <div>
            <dt>Payment</dt>
            <dd>{isTransfer ? 'None — shop transfer' : paymentText}</dd>
          </div>
          {/* Dropped entirely on a transfer rather than shown as a dash:
              there was no customer to ask, so an empty Customer row would
              read as one who declined. */}
          {!isTransfer && (
            <div>
              <dt>Customer</dt>
              <dd>{customerSummary(customer)}</dd>
            </div>
          )}
        </dl>

        <div className="modal-actions">
          <button
            type="button"
            ref={cancelRef}
            className="btn"
            onClick={onCancel}
            disabled={saving}
          >
            Go back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onConfirm}
            disabled={saving}
          >
            {saving
              ? 'Completing…'
              : isTransfer
                ? 'Confirm transfer'
                : `Confirm · ${money(total)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
